import { useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { message } from 'antd';
import type { Personnel } from '@/types/personnel';
import { Department as DepartmentType, UserDetail } from '@shared/types';
import type { UserUpdateRequest } from '@shared/services/users.service';
import { usersService } from '@/services/users.service';
import { departmentsService } from '@/services/departments.service';
import { handleApiError } from '@/utils/errorHandler';
import { formatDateDisplay } from '@/utils/dateHelpers';
import { fetchImageAsDataUri } from '@/contexts/AvatarContext';
import {
  savePersonnelFilterState,
  getPersonnelFilterState,
} from '@/features/personnel/personnelFilterStorage';
import { useNotificationSocketHeader } from '@/hooks/useNotificationSocketHeader';
import { useAuth } from '@/contexts/AuthContext';
import { apiService } from '@/services/api.service';

export interface UsePersonnelManagementResult {
  // filters
  searchText: string;
  setSearchText: (value: string) => void;
  selectedDepartment: string;
  setSelectedDepartment: (value: string) => void;

  // data
  filteredData: Personnel[];
  loading: boolean;
  error: string | null;
  departments: DepartmentType[];
  total: number;

  // pagination
  currentPage: number;
  pageSize: number;
  handlePageChange: (page: number, size?: number) => void;
  handlePageSizeChange: (current: number, size: number) => void;

  // modals
  isAddModalOpen: boolean;
  setIsAddModalOpen: (open: boolean) => void;
  isEditModalOpen: boolean;
  setIsEditModalOpen: (open: boolean) => void;
  selectedPersonnel: Personnel | null;
  setSelectedPersonnel: (personnel: Personnel | null) => void;

  // actions
  handleClearFilter: () => void;
  handleRefresh: () => Promise<void>;
  handleAddPersonnel: () => void;
  handleCloseAddModal: () => void;
  handleSubmitAddPersonnel: (data: any) => Promise<void>;
  handleEditPersonnel: (record: Personnel) => Promise<void>;
  handleCloseEditModal: () => void;
  handleSubmitEditPersonnel: (data: any) => Promise<void>;
  handleDeletePersonnel: (record: Personnel) => Promise<void>;
  handleLockPersonnel: (record: Personnel) => Promise<void>;
  handleApproveRequest: (record: Personnel) => Promise<UserUpdateRequest | null>;
  saveFilterStateBeforeNavigate: () => void;

  // pending requests
  usersWithPendingRequests: Set<number>;
  deletingId: string | null;
  lockingId: string | null;

  // avatar cache
  avatarCacheRef: React.MutableRefObject<Map<string, string>>;
  fetchAvatarAsDataUri: (imageUrl: string) => Promise<string | null>;
}

export const usePersonnelManagement = (): UsePersonnelManagementResult => {
  const { user: authUser } = useAuth();
  const location = useLocation();
  const [filteredData, setFilteredData] = useState<Personnel[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [departments, setDepartments] = useState<DepartmentType[]>([]);

  // Khôi phục filter state từ localStorage khi component mount
  const savedFilterState = getPersonnelFilterState();
  const [selectedDepartment, setSelectedDepartment] = useState<string>(
    savedFilterState?.selectedDepartment || 'Tất cả phòng ban'
  );
  const [searchText, _setSearchText] = useState<string>(
    savedFilterState?.searchText || ''
  );
  // Debounced search text để tránh gọi API quá nhiều khi user gõ
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>(
    savedFilterState?.searchText || ''
  );
  const [currentPage, setCurrentPage] = useState<number>(
    savedFilterState?.currentPage || 1
  );
  const [pageSize, setPageSize] = useState<number>(
    savedFilterState?.pageSize || 10
  );

  /**
   * Khi thay đổi searchText, luôn reset về trang 1.
   * Lưu ý: Dùng wrapper thay vì useEffect([searchText]) để không phá
   * logic khôi phục state (currentPage) từ localStorage khi quay lại màn hình.
   */
  const setSearchText = useCallback((value: string) => {
    _setSearchText(value);
    setCurrentPage(1);
  }, []);

  // Debounce searchText với 300ms delay (giống như useHeaderSearch)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 300);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [searchText]);

  // Khôi phục filter state khi quay lại từ trang khác
  const prevPathnameRef = useRef<string>(location.pathname);

  useEffect(() => {
    // Chỉ khôi phục state khi quay lại từ trang khác về trang quản lý nhân sự
    if (
      location.pathname === '/management/personnel' &&
      prevPathnameRef.current !== '/management/personnel'
    ) {
      const savedState = getPersonnelFilterState();
      if (savedState) {
        setSelectedDepartment(savedState.selectedDepartment);
        setSearchText(savedState.searchText);
        setCurrentPage(savedState.currentPage);
        setPageSize(savedState.pageSize);
      }
    }
    prevPathnameRef.current = location.pathname;
  }, [location.pathname]);

  const [total, setTotal] = useState<number>(0);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState<Personnel | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [lockingId, setLockingId] = useState<string | null>(null);
  const avatarCacheRef = useRef<Map<string, string>>(new Map());
  const [usersWithPendingRequests, setUsersWithPendingRequests] = useState<Set<number>>(new Set());
  const totalAdminCountRef = useRef<number | null>(null); // Cache tổng số admin

  // Fetch users từ API với error handling đầy đủ
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {
        page: currentPage,
        limit: pageSize,
      };

      if (debouncedSearchText) {
        params.search = debouncedSearchText;
      }

      if (selectedDepartment && selectedDepartment !== 'Tất cả phòng ban') {
        // Tìm department ID từ tên
        const dept = departments.find(d => d.name === selectedDepartment);
        if (dept) {
          params.departmentId = dept.id;
        }
      }

      const response = await usersService.getUsers(params);

      // Transform data từ API sang format của Personnel
      const transformedData: Personnel[] = response.data
        .filter((user: UserDetail) => user.role !== 'admin') // Loại bỏ admin
        .map((user: UserDetail) => {
          const dept = departments.find(d => d.id === user.departmentId);
          return {
            id: user.id.toString(),
            employeeCode: user.employeeCode || `NV${user.id}`,
            fullName: user.fullName,
            position: user.role || 'Chưa xác định',
            company: dept?.name || 'Chưa phân phòng ban',
            jobTitle: user.position || '',
            status: typeof user.isActive !== 'boolean' || typeof user.isBlocked !== 'boolean'
              ? 'unknown'
              : user.isBlocked || !user.isActive ? 'locked' : 'active',
            startDate: user.startDate ? formatDateDisplay(user.startDate) : formatDateDisplay(new Date()),
            avatar: user.avatar,
            role: user.role,
            roleId: user.roleId,
            managerId: user.managerId,
            email: user.email,
            departmentId: user.departmentId,
          };
        });

      setFilteredData(transformedData);
      
      // Tính total sau khi loại bỏ admin
      // Nếu chưa có cache tổng số admin, fetch một lần để đếm
      if (totalAdminCountRef.current === null) {
        try {
          // Fetch tất cả users (không pagination) để đếm tổng số admin
          const allUsersResponse = await usersService.getUsers({ limit: 10000 });
          const totalAdminCount = allUsersResponse.data.filter((user: UserDetail) => user.role === 'admin').length;
          totalAdminCountRef.current = totalAdminCount;
        } catch (err) {
          // Nếu fetch thất bại, fallback về cách tính cũ (trừ admin trong trang hiện tại)
          console.error('Error fetching total admin count:', err);
          const adminCountInCurrentPage = response.data.filter((user: UserDetail) => user.role === 'admin').length;
          const adjustedTotal = Math.max(0, response.total - adminCountInCurrentPage);
          setTotal(adjustedTotal);
          return;
        }
      }
      
      // Trừ tổng số admin đã cache khỏi total
      const adjustedTotal = Math.max(0, response.total - (totalAdminCountRef.current || 0));
      setTotal(adjustedTotal);
      setError(null);
    } catch (err: any) {
      handleApiError(err, 'Không thể tải danh sách nhân sự', false);
      setError(err?.message || 'Không thể tải danh sách nhân sự');
      message.error(err?.message || 'Không thể tải danh sách nhân sự');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearchText, selectedDepartment, departments]);

  // Fetch departments - cache vì ít thay đổi
  const fetchDepartments = useCallback(async () => {
    try {
      const response = await departmentsService.getDepartments({ limit: 100 });
      const depts = response.data.filter((dept: DepartmentType) => dept.isActive);
      setDepartments(depts);
    } catch (err) {
      // Không hiển thị lỗi cho departments vì không critical
      console.error('Error fetching departments:', err);
    }
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  useEffect(() => {
    // Chỉ fetch users khi đã có departments (để map department name)
    if (departments.length > 0 || selectedDepartment === 'Tất cả phòng ban') {
      fetchUsers();
    }
  }, [fetchUsers]);

  // Fetch pending update requests để hiển thị badge cảnh báo
  const fetchPendingUpdateRequests = useCallback(async () => {
    try {
      const response = await usersService.getUpdateRequests({
        status: 'pending',
        limit: 1000,
      });

      const userIds = new Set<number>();
      response.data.forEach((request) => {
        userIds.add(request.userId);
      });
      setUsersWithPendingRequests(userIds);
    } catch (err) {
      // Silently fail - không hiển thị lỗi cho feature này
      console.error('Error fetching pending update requests:', err);
    }
  }, []);

  // Realtime updates via notification socket
  const { isConnected, socket } = useNotificationSocketHeader({
    onNewNotification: () => {
      fetchPendingUpdateRequests();
    },
    onNotificationRead: () => {
      fetchPendingUpdateRequests();
    },
    onRefresh: () => {
      fetchPendingUpdateRequests();
    },
  });

  useEffect(() => {
    fetchPendingUpdateRequests();

    // Polling fallback: Refresh mỗi 5 giây nếu socket không kết nối
    const interval = setInterval(() => {
      if (!socket || !isConnected) {
        fetchPendingUpdateRequests();
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [fetchPendingUpdateRequests, socket, isConnected]);

  // Lưu filter state mỗi khi có thay đổi
  useEffect(() => {
    savePersonnelFilterState({
      searchText,
      selectedDepartment,
      currentPage,
      pageSize,
    });
  }, [searchText, selectedDepartment, currentPage, pageSize]);

  const handleClearFilter = useCallback(() => {
    setSelectedDepartment('Tất cả phòng ban');
    setSearchText('');
    setCurrentPage(1);
  }, []);

  // Khi đổi phòng ban, tự reset về trang 1 để tránh trường hợp đang ở trang cao nhưng dữ liệu filter ít
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDepartment]);

  const handlePageChange = useCallback((page: number, size?: number) => {
    setCurrentPage(page);
    if (size) {
      setPageSize(size);
    }
  }, []);

  const handlePageSizeChange = useCallback((current: number, size: number) => {
    setCurrentPage(current);
    setPageSize(size);
  }, []);

  const handleAddPersonnel = useCallback(() => {
    setIsAddModalOpen(true);
  }, []);

  const handleCloseAddModal = useCallback(() => {
    setIsAddModalOpen(false);
  }, []);

  const handleSubmitAddPersonnel = useCallback(async (data: any) => {
    try {
      const selectedRole = String(data.role ?? 'staff');
      const isRoleId = /^\d+$/.test(selectedRole);
      const department = departments.find((item) => item.name === data.department);
      const result = await usersService.createUser({
        fullName: data.fullName,
        email: data.email,
        password: data.password!,
        role: (!isRoleId ? selectedRole : undefined) as any,
        roleId:
          ['admin', 'hr'].includes(String(authUser?.role).toLowerCase()) && isRoleId
            ? Number(selectedRole)
            : undefined,
        position: data.position,
        departmentId: department?.id ?? authUser?.departmentId,
        managerId: data.managerId ? Number(data.managerId) : null,
      });

      message.success(result.message || 'Tạo nhân sự thành công');
      // Invalidate admin count cache khi có thay đổi
      totalAdminCountRef.current = null;
      await fetchUsers();
      await fetchPendingUpdateRequests();
      if (total % pageSize === 0) {
        setCurrentPage(1);
      }
    } catch (err: any) {
      handleApiError(err, 'Không thể tạo nhân sự');
      throw err;
    }
  }, [authUser, departments, fetchUsers, fetchPendingUpdateRequests, total, pageSize]);

  const handleEditPersonnel = useCallback(async (record: Personnel) => {
    try {
      // Fetch lại user detail từ API để có đầy đủ thông tin
      const userDetail = await usersService.getUserById(parseInt(record.id));

      const updatedRecord: Personnel = {
        ...record,
        role: userDetail.role || record.role,
        roleId: userDetail.roleId,
        managerId: userDetail.managerId,
        email: userDetail.email || record.email,
        departmentId: userDetail.departmentId || record.departmentId,
        fullName: userDetail.fullName,
        jobTitle: userDetail.position || record.jobTitle,
        workplace: userDetail.workplace,
      };

      setSelectedPersonnel(updatedRecord);
      setIsEditModalOpen(true);
    } catch (err: any) {
      message.error('Không thể tải thông tin chi tiết nhân sự');
      // Vẫn mở modal với data hiện có nếu fetch thất bại
      setSelectedPersonnel(record);
      setIsEditModalOpen(true);
    }
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditModalOpen(false);
    setSelectedPersonnel(null);
  }, []);

  const handleSubmitEditPersonnel = useCallback(async (data: any) => {
    try {
      if (!selectedPersonnel) return;

      const dept = departments.find(d => d.name === data.department);
      const userUpdateRules = (authUser?.permissions ?? []).filter(
        (rule) => rule.resource === 'user' && rule.action === 'update',
      );
      const allowedFields = userUpdateRules.flatMap((rule) => rule.fields ?? []);
      const hasUnrestrictedUpdate = userUpdateRules.some((rule) => !rule.fields?.length);
      const updatePayload: Record<string, unknown> = {
        fullName: data.fullName,
        email: data.email,
        position: data.position,
        workplace: data.workplace,
        departmentId: dept ? dept.id : undefined,
        managerId: data.managerId === 0 ? null : data.managerId ? Number(data.managerId) : null,
      };
      const isAdmin = authUser?.role === 'admin' || authUser?.permissions?.some(
        (rule) => rule.resource === 'all' && rule.action === 'manage',
      );
      const scopedPayload = isAdmin || hasUnrestrictedUpdate
        ? updatePayload
        : Object.fromEntries(
            Object.entries(updatePayload).filter(([field]) => allowedFields.includes(field)),
          );
      const userId = parseInt(selectedPersonnel.id);
      const result = await usersService.updateUser(userId, scopedPayload as any);
      const selectedRole = String(data.role ?? '');
      // Re-assign even when roleId matches: older accounts can have a stale
      // `user.role` string while `roleId` already points to the custom role.
      if (authUser?.role === 'admin' && /^\d+$/.test(selectedRole)) {
        await apiService.patch(`/rbac/users/${userId}/role`, {
          roleId: Number(selectedRole),
        });
      }

      message.success(result.message || 'Cập nhật nhân sự thành công');
      // Invalidate admin count cache khi có thay đổi
      totalAdminCountRef.current = null;
      await fetchUsers();
      await fetchPendingUpdateRequests();
    } catch (err: any) {
      handleApiError(err, 'Không thể cập nhật nhân sự');
      throw err;
    }
  }, [authUser, selectedPersonnel, departments, fetchUsers, fetchPendingUpdateRequests]);

  const handleDeletePersonnel = useCallback(async (record: Personnel) => {
    setDeletingId(record.id);
    try {
      const result = await usersService.deleteUser(parseInt(record.id));
      message.success(result.message || 'Xóa nhân sự thành công');
      // Invalidate admin count cache khi có thay đổi
      totalAdminCountRef.current = null;
      await fetchUsers();
      await fetchPendingUpdateRequests();
    } catch (err: any) {
      handleApiError(err, 'Không thể xóa nhân sự');
    } finally {
      setDeletingId(null);
    }
  }, [fetchUsers, fetchPendingUpdateRequests]);

  const handleLockPersonnel = useCallback(async (record: Personnel) => {
    setLockingId(record.id);
    try {
      if (record.status === 'active') {
        const result = await usersService.blockUser(parseInt(record.id), {
          reason: 'Khóa tài khoản từ quản lý nhân sự',
        });
        message.success(result.message || 'Khóa tài khoản thành công');
      } else {
        const result = await usersService.unblockUser(parseInt(record.id));
        message.success(result.message || 'Mở khóa tài khoản thành công');
      }
      // Invalidate admin count cache khi có thay đổi
      totalAdminCountRef.current = null;
      await fetchUsers();
      await fetchPendingUpdateRequests();
    } catch (err: any) {
      handleApiError(err, 'Không thể thay đổi trạng thái tài khoản');
    } finally {
      setLockingId(null);
    }
  }, [fetchUsers, fetchPendingUpdateRequests]);

  const handleApproveRequest = useCallback(async (record: Personnel) => {
    try {
      // Fetch pending update request for this user
      const response = await usersService.getUpdateRequests({
        status: 'pending',
        userId: parseInt(record.id),
        limit: 1,
      });

      if (response.data.length === 0) {
        message.warning('Không tìm thấy yêu cầu cập nhật đang chờ duyệt cho nhân viên này');
        return null;
      }

      const updateRequest = response.data[0];
      return updateRequest;
    } catch (err: any) {
      handleApiError(err, 'Không thể tải thông tin yêu cầu cập nhật');
      return null;
    }
  }, []);

  const saveFilterStateBeforeNavigate = useCallback(() => {
    savePersonnelFilterState({
      searchText,
      selectedDepartment,
      currentPage,
      pageSize,
    });
  }, [searchText, selectedDepartment, currentPage, pageSize]);

  const handleRefresh = useCallback(async () => {
    await Promise.all([fetchUsers(), fetchPendingUpdateRequests()]);
  }, [fetchUsers, fetchPendingUpdateRequests]);

  // Listen for avatar-updated event để clear cache và refresh avatar khi có user upload avatar mới
  useEffect(() => {
    const handleAvatarUpdated = (event: CustomEvent<{ userId: string }>) => {
      const userId = event.detail.userId;
      
      // Clear avatar cache cho user đó (tìm tất cả cache entries có chứa userId trong URL)
      const keysToDelete: string[] = [];
      avatarCacheRef.current.forEach((_, key) => {
        if (key.includes(userId) || key.includes(`/avatars/`)) {
          keysToDelete.push(key);
        }
      });
      keysToDelete.forEach(key => avatarCacheRef.current.delete(key));
      
      // Refresh lại data để lấy avatar mới
      handleRefresh();
    };

    window.addEventListener('avatar-updated', handleAvatarUpdated as EventListener);
    return () => {
      window.removeEventListener('avatar-updated', handleAvatarUpdated as EventListener);
    };
  }, [handleRefresh]);

  // Helper function để fetch avatar với cache
  // imageUrl đã được buildFullUrl() trước đó, nên chỉ cần fetch trực tiếp
  const fetchAvatarAsDataUri = useCallback(async (imageUrl: string): Promise<string | null> => {
    // Check cache first
    if (avatarCacheRef.current.has(imageUrl)) {
      return avatarCacheRef.current.get(imageUrl) || null;
    }

    // Fetch avatar từ URL (đã là full URL) và convert sang data URI
    try {
      const dataUri = await fetchImageAsDataUri(imageUrl);

      // Cache the result
      if (dataUri) {
        avatarCacheRef.current.set(imageUrl, dataUri);
      }

      return dataUri;
    } catch (err) {
      console.error('Error loading avatar:', err);
      return null;
    }
  }, []);

  return {
    // filters
    searchText,
    setSearchText,
    selectedDepartment,
    setSelectedDepartment,

    // data
    filteredData,
    loading,
    error,
    departments,
    total,

    // pagination
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,

    // modals
    isAddModalOpen,
    setIsAddModalOpen,
    isEditModalOpen,
    setIsEditModalOpen,
    selectedPersonnel,
    setSelectedPersonnel,

    // actions
    handleClearFilter,
    handleRefresh,
    handleAddPersonnel,
    handleCloseAddModal,
    handleSubmitAddPersonnel,
    handleEditPersonnel,
    handleCloseEditModal,
    handleSubmitEditPersonnel,
    handleDeletePersonnel,
    handleLockPersonnel,
    handleApproveRequest,
    saveFilterStateBeforeNavigate,

    // pending requests
    usersWithPendingRequests,
    deletingId,
    lockingId,

    // avatar cache
    avatarCacheRef,
    fetchAvatarAsDataUri,
  };
};

